import Resources from '@/scripts/libs/resources'
import wcs from '@/scripts/libs/wcs'
import { WcsLocales } from '@/scripts/libs/wcs/types/locales'
import { Cookies } from '@/scripts/libs/cookies'
import { GENESYS_CHAT_WAS_INITIALIZED } from '@/scripts/constants/cookies'
import { TRUE_LOWERCASE } from '@/scripts/constants/booleans'
import { TRACKER_CLICK } from '@/scripts/constants/tracker'
import RbGenesys from '@/elements/genesys/rb-genesys.vue'
import {
  ACTION_LOCK_SCRIPT,
  ACTION_SHOW_BUTTON,
  ACTION_HIDE_BUTTON,
} from '@/scripts/store/genesys/actions'

const { brand, genesys, locale } = wcs.getStoreConfig()
const genesysScriptURl = genesys.scriptUrl
const countryCode = WcsLocales.mapCountryCode(locale)
import Vue from 'vue'

class Genesys {
  private readonly localeForRoe = 'en-EU'
  private selectorForInjection = '.rb-app__footer'
  private vuexStore: any
  private multiAnalytics: any

  injectAndInit = ({ vuexStore, multiAnalytics }: { vuexStore: any; multiAnalytics: any }) => {
    this.vuexStore = vuexStore
    this.multiAnalytics = multiAnalytics

    if (!this.vuexStore.getters['genesys/areScriptsLoaded']) {
      this.vuexStore.dispatch(`genesys/${ACTION_LOCK_SCRIPT}`)
      this.configureGenesysLibrary()
      this.loadScriptAndInjectComponent()
    } else {
      this.openGenesysChat()
    }
  }

  private injectVueComponent = async () => {
    const parent = document.querySelector(this.selectorForInjection)

    const GenesysComponent = Vue.extend(RbGenesys)
    const instance = new GenesysComponent({
      store: this.vuexStore,
    })

    instance.$mount()
    parent?.appendChild(instance.$el)
  }

  private loadScriptAndInjectComponent = (): void => {
    Resources.loadResourcesWithCallback({
      resources: [{ src: genesysScriptURl }],
      callback: () => {
        this.sendData()
        this.bindReadyLauncher()
        this.bindConversationClosed()
        this.bindConversationOpened()
        this.injectVueComponent()
      },
    })
  }

  private configureGenesysLibrary(...args: any[]): void {
    const configuration = {
      deploymentId: genesys.deploymentId,
      environment: genesys.environment,
    }

    window.genesysJs = 'Genesys'

    window.Genesys =
      window.Genesys ||
      function () {
        ;(window.Genesys.q = window.Genesys.q || []).push(args)
      }

    window.Genesys.t = 1 * +new Date()

    window.Genesys.c = configuration
  }

  private sendData(): void {
    const formattedLocaleForGenesys = this.getLocaleForGenesys()
    const countryCodeForGenesys = this.getCountryCodeForGenesys()

    window.Genesys('command', 'Database.set', {
      messaging: {
        customAttributes: {
          webmsg_brandcode: brand,
          webmsg_country: countryCodeForGenesys,
          webmsg_language: formattedLocaleForGenesys,
        },
      },
    })
  }

  private bindConversationClosed = (): void => {
    window.Genesys('subscribe', 'Conversations.closed', (): void => {
      this.vuexStore.dispatch(`genesys/${ACTION_SHOW_BUTTON}`)
      this.multiAnalytics.trackEvent({
        event: TRACKER_CLICK,
        value: {
          data_element_id: 'X_X_ChatBot_Close',
        },
      })
    })
  }

  private bindConversationOpened = (): void => {
    window.Genesys('subscribe', 'Conversations.opened', (): void => {
      this.vuexStore.dispatch(`genesys/${ACTION_HIDE_BUTTON}`)
    })
  }

  private bindReadyLauncher = (): void => {
    if (this.genesysWasInitialized()) {
      this.vuexStore.dispatch(`genesys/${ACTION_SHOW_BUTTON}`)
    } else {
      window.Genesys('subscribe', 'Launcher.ready', this.onReadyLauncherCallback)
    }
  }

  private onReadyLauncherCallback = (): void => {
    if (!this.genesysWasInitialized()) {
      window.Genesys('subscribe', 'Messenger.ready', this.onReadyMessengerCallback)
    }
  }

  private onReadyMessengerCallback = (): void => {
    this.openGenesysChat()
    this.setGenesysCookie()
  }

  openGenesysChat(): void {
    window.Genesys('command', 'Messenger.open')
  }

  genesysWasInitialized(): boolean {
    return Cookies.get(GENESYS_CHAT_WAS_INITIALIZED) === TRUE_LOWERCASE
  }

  private setGenesysCookie = (): void => {
    Cookies.set(GENESYS_CHAT_WAS_INITIALIZED, TRUE_LOWERCASE)
  }

  private getLocaleForGenesys = () => {
    switch (locale) {
      case WcsLocales.EN_GB:
        return 'en-UK'
      case WcsLocales.EN_CY:
      case WcsLocales.EN_EE:
      case WcsLocales.EN_FI:
      case WcsLocales.EN_GR:
      case WcsLocales.EN_LT:
      case WcsLocales.EN_LU:
      case WcsLocales.EN_LV:
      case WcsLocales.EN_MT:
      case WcsLocales.EN_SI:
      case WcsLocales.EN_SK:
        return this.localeForRoe
      case WcsLocales.ZH_HK:
        return 'ca-HK'
      default:
        return locale
    }
  }

  private getCountryCodeForGenesys = () => {
    switch (countryCode) {
      case 'GB':
        return 'UK'
      default:
        return countryCode
    }
  }
}

export default new Genesys()
